import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const Packing = ({ formData, onFormDataChange }) => {
    const { packingObservations = [], nonConformanceObservations = '' } = formData;

    // State for holding data fetched from session storage for the table
    // const [packingData, setPackingData] = useState([]);

    useEffect(() => {
        // Destructure formData to get initial values
        const { packingObservations = [], nonConformanceObservations = '' } = formData;

        // Check if formData already has prefilled data
        const hasFormData =
            packingObservations.length > 0 || nonConformanceObservations.trim() !== '';

        if (!hasFormData) {
            // Step 1: Fetch metadata from sessionStorage
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));

            let prefilledPackingData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';

            // Step 2: Check metadata for prefilled data
            if (
                metadata &&
                metadata.post &&
                metadata.post.Packing_post
            ) {
                prefilledPackingData = metadata.post.Packing_post;
                prefilledObservations = prefilledPackingData.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: '',
                    observation2: '',
                }));
            }

            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

            // Step 3: Check inspectionData for observations and non-conformance data
            if (
                inspectionData &&
                inspectionData.PostLamination &&
                inspectionData.PostLamination.Packing_post
            ) {
                const { Packing, nonConformance } =
                    inspectionData.PostLamination.Packing_post;

                prefilledPackingData = Packing;
                prefilledObservations = Packing.map((item) => ({
                    product: item.product,
                    specifications: item.specifications,
                    frequency: item.frequency,
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));

                prefilledNonConformance = nonConformance || '';
            }

            // Step 4: Update formData only if prefilled data exists
            if (
                prefilledPackingData.length > 0 ||
                prefilledObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    packingObservations: prefilledObservations,
                    nonConformanceObservations: prefilledNonConformance,
                });
            }

        }
    }, [formData, onFormDataChange]);

    // Handle observation input change for the table (both Observation 1 and 2)
    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...packingObservations];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, packingObservations: updatedObservations });
    };

    // Handle non-conformance input change
    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformanceObservations: e.target.value });
    };

    // Function to send observations to the API
    const submitObservations = async () => {
        const incompleteObservation = packingObservations.some(
            (obs) => !obs.observation1.trim()
        );
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }

        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            const combinedData = packingObservations.map((item, index) => ({
                product: item.product,
                specifications: item.specifications,
                frequency: item.frequency,
                observation1: packingObservations[index]?.observation1 || '',
                observation2: packingObservations[index]?.observation2 || '',
            }));

            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PostLamination: {
                    Packing_post: {
                        Packing: combinedData,
                        nonConformance: nonConformanceObservations,
                    },
                },
            };

            await submitFormData(payload);
            alert('Observations submitted successfully!');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations.');
        }
    };

    // Render the packing table
    const renderPackingTable = () => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-4 text-gray-700">Packing</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {packingObservations.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${
                                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                                } hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={packingObservations[index]?.observation1 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation1', e.target.value)
                                        }
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={packingObservations[index]?.observation2 || ''}
                                        onChange={(e) =>
                                            handleObservationChange(index, 'observation2', e.target.value)
                                        }
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Packing Inspection</h3>
            {renderPackingTable()}
            {/* Non-Conformance Section */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-4 text-gray-700">Non-Conformance</h4>
                <textarea
                    value={nonConformanceObservations || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter non-conformance observations"
                    rows="4"
                    className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                />
            </div>
            {/* Submit Observations Button */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500 transition-colors duration-150"
            >
                Submit
            </button>
        </div>
    );
};

export default Packing;